import React, { useState, useEffect, useCallback } from "react";
import { Drawer, Button, Space, Alert, message, Spin, Typography } from "antd";
import { MailOutlined } from "@ant-design/icons";
import { scheduleService } from "../../../../api/enhancedApi";
import { emailTemplateApi } from "../../../../api/emailTemplateApi";
import GeneralEmailEditor from "../../../../components/EmailEditor/GeneralEmailEditor";
import AircraftTag from "../../../../components/AircraftTag";

const { Text } = Typography;

/**
 * 批量发送航班计划发布邮件抽屉组件
 * 用于处理多个计划的批量发布邮件发送
 *
 * @param {boolean} visible - 抽屉是否可见
 * @param {function} onClose - 关闭抽屉的回调
 * @param {array} schedules - 选中的计划数组
 * @param {function} onSuccess - 发送成功的回调
 */
const BatchPublishEmailSendDrawer = ({ visible, onClose, schedules, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [schedulesWithFlights, setSchedulesWithFlights] = useState([]);
    const [emailTemplate, setEmailTemplate] = useState(null);
    const [isEmailEditorVisible, setIsEmailEditorVisible] = useState(false);
    const [isEmailLoading, setIsEmailLoading] = useState(false);

    // 加载所有选中计划的航班数据
    const loadSchedulesData = useCallback(async () => {
        if (!schedules || schedules.length === 0) return;

        try {
            setLoading(true);
            const schedulesData = [];

            // 并行加载所有计划的航班数据
            await Promise.all(
                schedules.map(async (schedule) => {
                    try {
                        const flightsResponse = await scheduleService.getScheduleFlights(schedule.id, {
                            page: 1,
                            limit: 1000,
                        });

                        // 确保flights始终是一个数组
                        let flightsData = [];
                        if (flightsResponse?.data?.items && Array.isArray(flightsResponse.data.items)) {
                            flightsData = flightsResponse.data.items;
                        } else if (flightsResponse?.data && Array.isArray(flightsResponse.data)) {
                            flightsData = flightsResponse.data;
                        } else if (flightsResponse?.items && Array.isArray(flightsResponse.items)) {
                            flightsData = flightsResponse.items;
                        } else if (Array.isArray(flightsResponse)) {
                            flightsData = flightsResponse;
                        } else {
                            console.warn("无法识别的API响应格式:", flightsResponse);
                            flightsData = [];
                        }

                        schedulesData.push({
                            ...schedule,
                            flights: flightsData,
                        });
                    } catch (error) {
                        console.error(`加载计划 ${schedule.id} 的航班数据失败:`, error);
                        // 即使加载失败，也添加到数组中，但flights为空数组
                        schedulesData.push({
                            ...schedule,
                            flights: [],
                            loadError: error.message || "加载航班数据失败",
                        });
                    }
                }),
            );

            // 按照ID排序，确保顺序一致
            schedulesData.sort((a, b) => a.id - b.id);
            setSchedulesWithFlights(schedulesData);
        } catch (error) {
            console.error("批量加载计划数据失败:", error);
            message.error("加载计划数据失败，请重试");
        } finally {
            setLoading(false);
        }
    }, [schedules]);

    // 打开邮件编辑器
    const handleOpenEmailEditor = useCallback(async () => {
        try {
            setIsEmailLoading(true);
            // 优先使用发布邮件模板
            let templateResponse;
            try {
                templateResponse = await emailTemplateApi.getTemplateByCodeWithRecipients("SCHEDULE_PUBLISH_NOTIFICATION");
            } catch (error) {
                console.warn("获取发布邮件模板失败，使用确认邮件模板作为备选:", error);
                // 如果没有发布邮件模板，回退到确认邮件模板
                templateResponse = await emailTemplateApi.getTemplateByCodeWithRecipients("SCHEDULE_AAE_NEW_CONFIRMATION");
            }

            if (templateResponse.data) {
                setEmailTemplate({
                    ...templateResponse.data,
                    // 如果使用的是确认邮件模板作为备选，修改主题模板
                    subject_template: templateResponse.data.template_code === "SCHEDULE_AAE_NEW_CONFIRMATION" 
                        ? "航班计划发布通知 - {aircraftText} on {dateRangeText}" 
                        : templateResponse.data.subject_template
                });
                console.log("[BatchPublishEmailSendDrawer] 已加载邮件模板和收件人配置:", templateResponse.data);
                setIsEmailEditorVisible(true);
            } else {
                throw new Error("获取邮件模板失败：服务器返回空数据");
            }
        } catch (error) {
            console.error("获取邮件模板失败:", error);
            message.error(`获取邮件模板失败: ${error.message || "未知错误"}`);
        } finally {
            setIsEmailLoading(false);
        }
    }, []);

    // 当抽屉打开时加载数据
    useEffect(() => {
        if (visible && schedules?.length > 0) {
            loadSchedulesData();
        }
    }, [visible, schedules, loadSchedulesData]);

    // 处理邮件发送
    const handleEmailSubmit = async (emailData) => {
        try {
            setIsEmailLoading(true);

            // 使用统一的邮件API
            const { sendBatchSchedulePublishEmailV2 } = await import("../../../../api/scheduleEmailApi");

            // 转换前端数据格式为后端期望的格式
            const convertEmailField = (field) => {
                if (Array.isArray(field)) {
                    return field.length > 0 ? field.join(", ") : null;
                }
                return field || null;
            };

            // 构建请求数据
            const requestData = {
                to: convertEmailField(emailData.to) || "",
                cc: convertEmailField(emailData.cc),
                bcc: convertEmailField(emailData.bcc),
                subject: emailData.subject || "",
                rendered_html: emailData.contentHtml || "",
                content_block_context: emailData.contentBlockContext || {
                    template_code: emailTemplate?.template_code || "SCHEDULE_PUBLISH_NOTIFICATION",
                    instances: [],
                    business_context_snapshot: {},
                },
                attachments: emailData.attachments || [],
                // 添加多计划ID
                schedule_ids: schedulesWithFlights.map((schedule) => schedule.id),
            };

            console.log("[BatchPublishEmailSendDrawer] 发送请求数据:", requestData);

            // 调用批量发送发布邮件V2 API
            const response = await sendBatchSchedulePublishEmailV2(requestData);

            if (response.success) {
                message.success(response.message || "发布邮件发送成功");
                setIsEmailEditorVisible(false);
                onSuccess && onSuccess();
            } else {
                message.error(response.message || "邮件发送失败");
            }
        } catch (error) {
            console.error("发送邮件失败:", error);

            // 安全地提取错误消息
            let errorMessage = "发送邮件时发生错误";

            if (error.response?.data?.detail) {
                if (Array.isArray(error.response.data.detail)) {
                    const validationErrors = error.response.data.detail
                        .map((err) => {
                            const field = err.loc ? err.loc.join(".") : "未知字段";
                            return `${field}: ${err.msg}`;
                        })
                        .join("; ");
                    errorMessage = `数据验证失败: ${validationErrors}`;
                } else if (typeof error.response.data.detail === "string") {
                    errorMessage = error.response.data.detail;
                }
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            message.error(errorMessage);
        } finally {
            setIsEmailLoading(false);
        }
    };

    // 关闭邮件编辑器
    const handleEmailCancel = () => {
        setIsEmailEditorVisible(false);
    };

    // 生成邮件上下文数据
    const generateEmailContextData = useCallback(() => {
        if (schedulesWithFlights.length === 0) return {};

        // 获取所有飞机注册号
        const aircraftRegistrations = [...new Set(schedulesWithFlights.map((schedule) => schedule.aircraft?.registration).filter(Boolean))];

        // 获取计划的日期范围 - 取第一个计划的日期范围即可
        const firstSchedule = schedulesWithFlights[0];
        const dateRange = {
            start_date: firstSchedule.start_date,
            end_date: firstSchedule.end_date,
        };

        // 为邮件主题准备飞机注册号文本
        const aircraftText = aircraftRegistrations.join(" & ");

        // 为邮件主题准备日期范围文本 (例如: 16JUN-19JUN)
        const startDate = new Date(dateRange.start_date);
        const endDate = new Date(dateRange.end_date);

        const formatDateForSubject = (date) => {
            const day = date.getDate().toString().padStart(2, "0");
            const month = date.toLocaleString("en-US", { month: "short" }).toUpperCase();
            return `${day}${month}`;
        };

        const dateRangeText = `${formatDateForSubject(startDate)}-${formatDateForSubject(endDate)}`;

        // 为每个计划生成单独的内容块
        const contentBlocks = [];

        // 添加基本信息内容块
        contentBlocks.push({
            id: "schedule_basic_info",
            type: "multiScheduleInfo",
            title: "计划基本信息",
            data: {
                schedules: schedulesWithFlights,
                aircraftRegistrations,
                dateRange,
                aircraftText,
                dateRangeText,
            },
            // 行内显示格式：9H-FLM & 9H-WFF on 16JUN-19JUN
            inlineText: `${aircraftText} on ${dateRangeText}`,
            business_source: {
                entity_type: "Schedule",
                entity_ids: schedulesWithFlights.map((schedule) => schedule.id),
            },
        });

        // V3 新增：使用 dataSources 格式为多个计划创建统一的内容块
        // 将每个计划的航班数据作为独立的数据源
        const flightListDataSources = schedulesWithFlights.map((schedule) => ({
            // 每个数据源包含该计划的航班列表和计划信息
            flights: schedule.flights || [],
            scheduleInfo: {
                id: schedule.id,
                name: schedule.name,
                aircraft_registration: schedule.aircraft?.registration,
                start_date: schedule.start_date,
                end_date: schedule.end_date,
            },
        }));

        // 创建单个内容块，但包含多个数据源
        contentBlocks.push({
            id: "flight_list", // 统一使用 flight_list 作为ID，匹配模板
            type: "scheduleFlightList",
            title: "航班列表",
            // V3 新增：使用 dataSources 而不是单个 data
            dataSources: flightListDataSources,
            // 为行内显示准备的文本
            inlineText: `${aircraftText} on ${dateRangeText}`,
            business_source: {
                entity_type: "ScheduleFlight",
                entity_ids: schedulesWithFlights.flatMap((schedule) => (schedule.flights || []).filter((flight) => flight.id).map((flight) => flight.id)),
            },
        });

        // 构建V2模式的上下文数据
        const contextData = {
            // V1模式兼容格式
            schedule: firstSchedule,
            flights: firstSchedule.flights || [],
            // V2 模式
            contentBlocks: contentBlocks,
        };

        return contextData;
    }, [schedulesWithFlights]);

    // 渲染选中的计划列表
    const renderSelectedSchedules = () => {
        if (loading) {
            return (
                <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <Spin size="large" />
                    <p style={{ marginTop: 16 }}>正在加载计划数据...</p>
                </div>
            );
        }

        if (schedulesWithFlights.length === 0) {
            return <Alert message="未选择任何计划" type="warning" showIcon />;
        }

        return (
            <div className="selected-schedules-list">
                <Alert
                    message="已选择以下航班计划"
                    description={
                        <ul style={{ paddingLeft: "20px", marginTop: "8px" }}>
                            {schedulesWithFlights.map((schedule) => (
                                <li key={schedule.id} style={{ marginBottom: "8px" }}>
                                    <div>
                                        <Text strong>{schedule.name || "未命名计划"}</Text>
                                        {schedule.aircraft && (
                                            <span style={{ marginLeft: "8px" }}>
                                                <AircraftTag registration={schedule.aircraft.registration} />
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ color: "#666", fontSize: "12px" }}>
                                        航班数量: {schedule.flights?.length || 0}
                                        {schedule.loadError && (
                                            <Text type="danger" style={{ marginLeft: "8px" }}>
                                                (加载错误: {schedule.loadError})
                                            </Text>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    }
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                />
            </div>
        );
    };

    return (
        <>
            <Drawer
                title={
                    <span>
                        <MailOutlined style={{ marginRight: 8 }} />
                        批量发送航班计划发布邮件
                    </span>
                }
                placement="right"
                width="600px"
                onClose={onClose}
                open={visible}
                extra={
                    <Space>
                        <Button onClick={onClose}>取消</Button>
                        <Button type="primary" onClick={handleOpenEmailEditor} disabled={loading || schedulesWithFlights.length === 0} loading={isEmailLoading} icon={<MailOutlined />}>
                            编辑邮件内容
                        </Button>
                    </Space>
                }
                destroyOnHidden={true}
            >
                <div className="batch-email-drawer-content">
                    <div className="batch-email-instructions">
                        <Alert
                            message="批量发送发布邮件"
                            description={
                                <div>
                                    <p>您可以为多个航班计划同时发送一封发布邮件：</p>
                                    <ul>
                                        <li>系统将合并所有选中计划的航班数据</li>
                                        <li>邮件中将包含所有计划的详细信息</li>
                                        <li>发送后，将记录发布邮件发送日志</li>
                                    </ul>
                                    <p style={{ color: "#666", fontSize: "12px" }}>注意：请确保选择的计划都已经过航司确认，且具有相似的时间范围和目的。</p>
                                </div>
                            }
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    </div>

                    {renderSelectedSchedules()}
                </div>
            </Drawer>

            {/* 邮件编辑器 */}
            {isEmailEditorVisible && emailTemplate && (
                <GeneralEmailEditor
                    visible={isEmailEditorVisible}
                    onSubmit={handleEmailSubmit}
                    onCancel={handleEmailCancel}
                    title="发送航班计划发布邮件"
                    subjectTemplate={emailTemplate?.subject_template || "航班计划发布通知 - {aircraftText} on {dateRangeText}"}
                    initialSubject={`航班计划发布通知 - 多个计划`}
                    templateContent={emailTemplate?.body_html || ""}
                    templateRecipients={{
                        default_to: emailTemplate?.default_to,
                        default_cc: emailTemplate?.default_cc,
                    }}
                    businessContext={generateEmailContextData()}
                    enableV2Mode={true}
                />
            )}
        </>
    );
};

export default BatchPublishEmailSendDrawer; 